import React, { useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';

import { MODAL_WIDTH } from '@atlassian/agent-studio-skills/constants';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { editSkillEntrypoint } from '@atlassian/custom-skills/edit-skill';
import { ModalButtonTrigger } from '@atlassian/entry-points/modal-button-trigger';
import { useRovoSkillsInvalidation } from '@atlassian/rovo-skills/services/rovo-skills-config-provider';

import { messages } from './messages';

// Match the skills directory modal width so the edit surface is consistent.
const modalProps = {
	width: MODAL_WIDTH,
};

/** Which flavour of the custom skill card the edit trigger is rendered in. */
export type CustomSkillCardType = 'create' | 'update';

export type EditSkillModalButtonProps = {
	/** Cloud (site) id for the current conversation. */
	cloudId: string;
	/** ARI of the custom skill to edit. */
	skillAri: string;
	/**
	 * The card the button was rendered from, so edits started from a freshly created
	 * skill can be told apart from edits started from an updated one in analytics.
	 */
	cardType: CustomSkillCardType;
};

/**
 * Footer action that opens the "Edit skill" modal.
 *
 * Uses {@link ModalButtonTrigger} so the modal open/close behaviour stays
 * consistent with the other entry-point-driven modals in chat.
 */
export const EditSkillModalButton = ({
	cloudId,
	skillAri,
	cardType,
}: EditSkillModalButtonProps): React.JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { invalidateSkillLibrary } = useRovoSkillsInvalidation();
	const entryPointParams = useMemo(() => ({ cloudId, skillAri }), [cloudId, skillAri]);

	const entryPointProps = useMemo(
		() => ({ onSaved: invalidateSkillLibrary }),
		[invalidateSkillLibrary],
	);

	const handleClick = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'editCustomSkillCard',
			attributes: {
				cardType,
			},
		});
	}, [sendAnalyticsEvent, cardType]);

	return (
		<ModalButtonTrigger
			entryPoint={editSkillEntrypoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			modalProps={modalProps}
			onClick={handleClick}
			testId="custom-skill-card-edit"
		>
			<FormattedMessage {...messages.editLabel} />
		</ModalButtonTrigger>
	);
};
